import { http } from '@/api/http';
import type {
  CreateUserRequestDto,
  PageUserDto,
  UpdateUserProfileBody,
  UserDemographicsDto,
  UserDto,
} from '@/features/admin-users/endpoints';

export type { UpdateUserProfileBody, UserDemographicsDto };

/** Wire DTO for a role in the assignable-role catalogue (camelCase, matching {@code RoleDTO}). */
export interface PlatformRoleDto {
  name?: string;
  description?: string | null;
  builtIn?: boolean | null;
}

/** Wire DTO for a user's role assignment (camelCase, matching {@code UserRoleAssignmentDTO}). */
export interface PlatformUserRolesDto {
  userGuid?: string;
  roleNames?: string[];
}

function encode(id: string): string {
  return encodeURIComponent(id);
}

/**
 * Raw transport for the GLOBAL (platform-plane) user directory (PTV2-1383).
 *
 * These endpoints hit `/api/v1/platform/users`, a net-new backend surface that
 * ALWAYS targets the global plane regardless of any active-tenant selection —
 * unlike `/api/v1/users` (admin-users), which is tenant-routed by the caller's
 * active tenant. The wire shapes are identical to the tenant user directory, so
 * the `UserDto` / `PageUserDto` / `CreateUserRequestDto` types are reused from
 * `admin-users/endpoints`.
 */
export const platformUsersEndpoints = {
  /** GET /platform/users?page=N&size=N — global user directory. */
  getUsers: (
    params: { page: number; size: number },
    options?: { signal?: AbortSignal },
  ): Promise<PageUserDto> =>
    http.get<PageUserDto>('/platform/users', {
      ...options,
      query: { page: params.page, size: params.size },
    }),

  /**
   * GET /platform/users/search?q=…&page=&size= — case-insensitive substring
   * match across name, full_name, email, scoped to the global plane. {@code q}
   * is required (1–200 chars); the BE rejects blank queries with a 400.
   */
  searchUsers: (
    params: { q: string; page?: number; size?: number },
    options?: { signal?: AbortSignal },
  ): Promise<PageUserDto> =>
    http.get<PageUserDto>('/platform/users/search', {
      ...options,
      query: {
        q: params.q,
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.size !== undefined ? { size: params.size } : {}),
      },
    }),

  /** POST /platform/users — create a global user. */
  createUser: (
    body: CreateUserRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<UserDto> => http.post<UserDto>('/platform/users', body, options),

  /** DELETE /platform/users/{uuid} — delete a global user. */
  deleteUser: (uuid: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`/platform/users/${encodeURIComponent(uuid)}`, options),

  /** GET /platform/users/{uuid} — a single global user by guid. */
  getUser: (uuid: string, options?: { signal?: AbortSignal }): Promise<UserDto> =>
    http.get<UserDto>(`/platform/users/${encode(uuid)}`, options),

  /** GET /platform/users/assignable-roles — the global role catalogue for the role picker. */
  getAssignableRoles: (options?: { signal?: AbortSignal }): Promise<PlatformRoleDto[]> =>
    http.get<PlatformRoleDto[]>('/platform/users/assignable-roles', options),

  /** GET /platform/users/{uuid}/demographics — a global user's editable name parts. */
  getUserDemographics: (
    uuid: string,
    options?: { signal?: AbortSignal },
  ): Promise<UserDemographicsDto> =>
    http.get<UserDemographicsDto>(`/platform/users/${encode(uuid)}/demographics`, options),

  /** PUT /platform/users/{uuid} — update a global user's profile (first/last/title). */
  updateUserProfile: (
    uuid: string,
    body: UpdateUserProfileBody,
    options?: { signal?: AbortSignal },
  ): Promise<UserDto> => http.put<UserDto>(`/platform/users/${encode(uuid)}`, body, options),

  /** GET /platform/users/{uuid}/roles — a global user's assigned role names. */
  getUserRoles: (uuid: string, options?: { signal?: AbortSignal }): Promise<PlatformUserRolesDto> =>
    http.get<PlatformUserRolesDto>(`/platform/users/${encode(uuid)}/roles`, options),

  /** PUT /platform/users/{uuid}/roles — replace a global user's roles. */
  replaceUserRoles: (
    uuid: string,
    roleNames: readonly string[],
    options?: { signal?: AbortSignal },
  ): Promise<PlatformUserRolesDto> =>
    http.put<PlatformUserRolesDto>(`/platform/users/${encode(uuid)}/roles`, { roleNames }, options),

  /** POST /platform/users/{uuid}/deactivate — soft-deactivate a global user. */
  deactivateUser: (uuid: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.post<undefined>(`/platform/users/${encode(uuid)}/deactivate`, undefined, options),

  /** POST /platform/users/{uuid}/activate — reactivate a global user. */
  activateUser: (uuid: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.post<undefined>(`/platform/users/${encode(uuid)}/activate`, undefined, options),
};
